import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import {
  type IntakeQuestion,
  type IntakeQuestionKind,
  type IntakeQuestionOption,
  useCompleteIntake,
  useSetupChecklist,
} from "@/hooks/useSetupChecklist";
import {
  type BusinessProfile,
  type EntityType,
  type FamilyProfile,
  type FilingStatus,
  useBusinessProfile,
  useFamilyProfile,
  useUpdateBusinessProfile,
  useUpdateFamilyProfile,
} from "@/hooks/useProfile";

// =============================================================================
// IntakeWizard — the one-question-at-a-time conversational wizard that drives
// the very first load for any user whose FamilyProfile.OnboardingCompletedAt
// is null.
//
// Design contract (why this file is separate from the rest of the setup UI):
//   * We snapshot the initial list of questions on mount. Saving an answer
//     invalidates the setup-checklist query, which would normally shrink the
//     list mid-flow and break the step counter. The snapshot keeps the flow
//     stable for a single session.
//   * For the one branch we know about — "hasEntity = yes" → ask entity type
//     + formation state — we splice the branch questions into the snapshot
//     locally so the user sees them appear immediately, without a round trip.
//   * Each answer saves to the correct backing field via the already-existing
//     useUpdateFamilyProfile / useUpdateBusinessProfile mutations. We merge
//     with whatever the server currently has so we never blank out a field
//     the user set previously.
//   * "Skip" (only offered on !isRequired questions) advances without saving.
//   * "Back" returns to the previous step and keeps the previously-chosen
//     value pre-filled so a mistaken answer can be corrected.
//   * Finishing (or the header's "Skip for now" link) calls
//     POST /api/setup/intake-complete to stamp OnboardingCompletedAt so the
//     wizard never re-triggers.
// =============================================================================

export interface IntakeWizardProps {
  /**
   * Called when the user finishes (or skips) the wizard AND the completion
   * POST has resolved. The caller is expected to move the user into the
   * rest of the app (AppShell release, checklist show, etc.).
   */
  onComplete?: () => void;

  /**
   * Render as a full-screen overlay rather than inline. Used by the route
   * guard in AppShell. Defaults to false for inline use inside the Setup
   * Center page.
   */
  fullScreen?: boolean;
}

export function IntakeWizard({ onComplete, fullScreen = false }: IntakeWizardProps) {
  const checklist = useSetupChecklist();
  const family = useFamilyProfile();
  const business = useBusinessProfile();
  const saveFamily = useUpdateFamilyProfile();
  const saveBusiness = useUpdateBusinessProfile();
  const completeIntake = useCompleteIntake();

  // Snapshot the initial question list so mutation-triggered invalidations
  // don't yank questions out from under the user mid-flow.
  const [questions, setQuestions] = useState<IntakeQuestion[] | null>(null);
  const snapshottedFromRef = useRef<IntakeQuestion[] | null>(null);

  useEffect(() => {
    if (questions) return;
    const fresh = checklist.data?.intake;
    if (!fresh) return;
    // Only snapshot once and only when we actually have questions.
    if (snapshottedFromRef.current === fresh) return;
    if (fresh.length > 0) {
      snapshottedFromRef.current = fresh;
      setQuestions(fresh);
    }
  }, [checklist.data?.intake, questions]);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Loading + empty states.
  // ---------------------------------------------------------------------------

  const isInitialLoading =
    (checklist.isLoading && !checklist.data) ||
    (family.isLoading && !family.data) ||
    (business.isLoading && !business.data);

  if (isInitialLoading) {
    return (
      <Shell fullScreen={fullScreen}>
        <div className="flex items-center gap-3 py-16">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Pulling your profile…
          </span>
        </div>
      </Shell>
    );
  }

  // Planner returned no questions: nothing to ask, but the guard still
  // expects us to stamp completion so we don't flash back. We call it once
  // and hand off to onComplete.
  if (questions && questions.length === 0) {
    return <AutoCompleter onDone={onComplete} completeIntake={completeIntake} />;
  }

  if (!questions) {
    // Checklist arrived but intake array was empty AND we haven't snapshotted
    // anything yet — same path as above.
    return <AutoCompleter onDone={onComplete} completeIntake={completeIntake} />;
  }

  const total = questions.length;
  const current = questions[Math.min(step, total - 1)];
  const progressPct = Math.round(((step + 1) / total) * 100);

  // ---------------------------------------------------------------------------
  // Answer persistence: maps a question key to the right mutation + shape.
  // ---------------------------------------------------------------------------

  async function persistAnswer(key: string, raw: unknown): Promise<boolean> {
    const fam = family.data;
    const biz = business.data;
    if (!fam) {
      setSaveError("Profile failed to load. Please refresh.");
      return false;
    }
    try {
      switch (key) {
        case "focusArea": {
          await saveFamily.mutateAsync({ ...fam, focusArea: raw as string });
          return true;
        }
        case "filingStatus": {
          await saveFamily.mutateAsync({
            ...fam,
            filingStatus: raw as FilingStatus,
          });
          return true;
        }
        case "stateOfResidence": {
          await saveFamily.mutateAsync({
            ...fam,
            stateOfResidence: raw as string,
          });
          return true;
        }
        case "hasEntity": {
          const yes = raw === true;
          await saveFamily.mutateAsync({ ...fam, hasBusinessEntity: yes });
          // Branch: splice entity-details questions in right after this one
          // so the user sees them appear immediately without waiting for the
          // planner round trip. We only inject if they said yes AND the
          // questions aren't already in the list.
          if (yes) {
            setQuestions((prev) => {
              if (!prev) return prev;
              if (prev.some((q) => q.key === "entityType")) return prev;
              const ix = prev.findIndex((q) => q.key === "hasEntity");
              if (ix < 0) return prev;
              const branchInsert: IntakeQuestion[] = [];
              if (!biz?.legalName) {
                branchInsert.push({
                  key: "entityType",
                  prompt: "What type of entity is it?",
                  helpText:
                    "We'll use this to decide which forms, deadlines, and tax plays apply to you.",
                  kind: "EntityType",
                  options: [...ENTITY_TYPE_OPTIONS],
                  isRequired: true,
                });
              }
              if (!biz?.formationState) {
                branchInsert.push({
                  key: "entityFormationState",
                  prompt: "What state was it formed in?",
                  helpText:
                    "Drives annual-report, franchise-tax, and registered-agent reminders. Not sure? Skip for now.",
                  kind: "State",
                  isRequired: false,
                });
              }
              if (branchInsert.length === 0) return prev;
              return [
                ...prev.slice(0, ix + 1),
                ...branchInsert,
                ...prev.slice(ix + 1),
              ];
            });
          }
          return true;
        }
        case "entityType": {
          const base: Partial<BusinessProfile> = biz ?? { legalName: "" };
          await saveBusiness.mutateAsync({
            ...base,
            legalName: base.legalName ?? "",
            entityType: raw as EntityType,
          });
          return true;
        }
        case "entityFormationState": {
          const base: Partial<BusinessProfile> = biz ?? { legalName: "" };
          await saveBusiness.mutateAsync({
            ...base,
            legalName: base.legalName ?? "",
            formationState: raw as string,
          });
          return true;
        }
        case "isHsaEligible": {
          await saveFamily.mutateAsync({
            ...fam,
            isHsaEligible: raw === true,
          });
          return true;
        }
        default: {
          // Unknown key — don't swallow it, but don't break the flow either.
          setSaveError(`Unknown question "${key}". Skipping for now.`);
          return true;
        }
      }
    } catch (e) {
      setSaveError(
        e instanceof Error
          ? e.message
          : "Couldn't save that answer. Try again?",
      );
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation.
  // ---------------------------------------------------------------------------

  async function handleAnswer(value: unknown) {
    setSaveError(null);
    setAnswers((a) => ({ ...a, [current.key]: value }));
    const ok = await persistAnswer(current.key, value);
    if (!ok) return;
    advance();
  }

  async function handleSkip() {
    setSaveError(null);
    advance();
  }

  function handleBack() {
    setSaveError(null);
    setStep((s) => Math.max(0, s - 1));
  }

  async function advance() {
    // We re-read `questions` here through state closure; React may have
    // extended the list (entity branch) since we entered this function.
    const nextStep = step + 1;
    const latestTotal = questions?.length ?? total;
    if (nextStep >= latestTotal) {
      await finishWizard();
    } else {
      setStep(nextStep);
    }
  }

  async function finishWizard() {
    try {
      await completeIntake.mutateAsync();
      onComplete?.();
    } catch (e) {
      setSaveError(
        e instanceof Error
          ? e.message
          : "Couldn't finalize onboarding. Try again?",
      );
    }
  }

  async function handleSkipAll() {
    setSaveError(null);
    await finishWizard();
  }

  const isSaving =
    saveFamily.isPending || saveBusiness.isPending || completeIntake.isPending;
  const initialValue = answers[current.key] ?? readInitialValue(current, family.data, business.data);

  // ---------------------------------------------------------------------------
  // Render.
  // ---------------------------------------------------------------------------

  return (
    <Shell fullScreen={fullScreen}>
      <div className="flex items-center justify-between gap-4 pb-4">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          Question {step + 1} of {total}
        </div>
        <button
          type="button"
          onClick={handleSkipAll}
          disabled={isSaving}
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-50"
        >
          Skip the interview
        </button>
      </div>

      <div className="mb-8 h-1.5 w-full overflow-hidden rounded-full bg-muted/40">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
          {current.prompt}
        </h1>
        {current.helpText && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {current.helpText}
          </p>
        )}
      </div>

      <div className="mt-6">
        <QuestionInput
          question={current}
          initialValue={initialValue}
          onSubmit={handleAnswer}
          disabled={isSaving}
        />
      </div>

      {saveError && (
        <p className="mt-4 text-sm text-destructive">{saveError}</p>
      )}

      <div className="mt-8 flex items-center justify-between border-t border-border/60 pt-5">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleBack}
            disabled={step === 0 || isSaving}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </Button>
          {!current.isRequired && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleSkip}
              disabled={isSaving}
            >
              <SkipForward className="h-3.5 w-3.5" />
              Skip
            </Button>
          )}
        </div>

        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {current.isRequired ? "Required" : "Optional — skip anytime"}
        </p>
      </div>
    </Shell>
  );
}

// =============================================================================
// Shell — full-screen overlay vs inline card, sharing the same padding.
// =============================================================================

function Shell({
  children,
  fullScreen,
}: {
  children: React.ReactNode;
  fullScreen: boolean;
}) {
  if (!fullScreen) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-8 shadow-sm">
        {children}
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gradient-to-br from-background via-background to-primary/10 px-4 py-10">
      <div className="w-full max-w-2xl rounded-xl border border-border/60 bg-card p-8 shadow-xl">
        {children}
      </div>
    </div>
  );
}

// =============================================================================
// AutoCompleter — edge case: planner returned zero questions but we still need
// to stamp OnboardingCompletedAt so the guard stops redirecting. Fires once
// and hands off.
// =============================================================================

function AutoCompleter({
  onDone,
  completeIntake,
}: {
  onDone?: () => void;
  completeIntake: ReturnType<typeof useCompleteIntake>;
}) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    completeIntake
      .mutateAsync()
      .then(() => onDone?.())
      .catch(() => {
        /* keep the user where they are; no loud error */
      });
  }, [completeIntake, onDone]);
  return (
    <div className="flex items-center gap-3 py-16">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Finishing setup…</span>
    </div>
  );
}

// =============================================================================
// QuestionInput — kind-specific UI.
// =============================================================================

function QuestionInput({
  question,
  initialValue,
  onSubmit,
  disabled,
}: {
  question: IntakeQuestion;
  initialValue: unknown;
  onSubmit: (value: unknown) => void;
  disabled: boolean;
}) {
  switch (question.kind as IntakeQuestionKind) {
    case "YesNo":
      return (
        <YesNoInput
          initial={initialValue as boolean | undefined}
          onSubmit={(v) => onSubmit(v)}
          disabled={disabled}
        />
      );
    case "FilingStatus":
      return (
        <ChoiceInput
          options={FILING_STATUS_OPTIONS}
          initial={(initialValue as string) ?? ""}
          onSubmit={onSubmit}
          disabled={disabled}
        />
      );
    case "State":
      return (
        <StateInput
          initial={(initialValue as string) ?? ""}
          onSubmit={onSubmit}
          disabled={disabled}
          required={question.isRequired}
        />
      );
    case "Choice":
      return (
        <ChoiceInput
          options={question.options ?? []}
          initial={(initialValue as string) ?? ""}
          onSubmit={onSubmit}
          disabled={disabled}
        />
      );
    case "EntityType":
      return (
        <ChoiceInput
          options={question.options ?? ENTITY_TYPE_OPTIONS}
          initial={(initialValue as string) ?? ""}
          onSubmit={onSubmit}
          disabled={disabled}
        />
      );
    case "Number":
      return (
        <NumberInput
          initial={initialValue as number | null | undefined}
          onSubmit={onSubmit}
          disabled={disabled}
          required={question.isRequired}
        />
      );
    case "Date":
      return (
        <DateInput
          initial={(initialValue as string) ?? ""}
          onSubmit={onSubmit}
          disabled={disabled}
          required={question.isRequired}
        />
      );
    default:
      return (
        <p className="text-sm text-destructive">
          Unsupported question type: {question.kind}
        </p>
      );
  }
}

// --- Yes / No ---------------------------------------------------------------

function YesNoInput({
  initial,
  onSubmit,
  disabled,
}: {
  initial: boolean | undefined;
  onSubmit: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <BigChoiceButton
        selected={initial === true}
        disabled={disabled}
        label="Yes"
        onClick={() => onSubmit(true)}
      />
      <BigChoiceButton
        selected={initial === false}
        disabled={disabled}
        label="No / not yet"
        onClick={() => onSubmit(false)}
      />
    </div>
  );
}

// --- Choice (focusArea, filingStatus, entityType) ---------------------------

function ChoiceInput({
  options,
  initial,
  onSubmit,
  disabled,
}: {
  options: ReadonlyArray<IntakeQuestionOption>;
  initial: string;
  onSubmit: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-2.5">
      {options.map((opt) => (
        <BigChoiceButton
          key={opt.value}
          selected={initial === opt.value}
          disabled={disabled}
          label={opt.label}
          description={opt.description ?? undefined}
          onClick={() => onSubmit(opt.value)}
        />
      ))}
    </div>
  );
}

// --- State picker -----------------------------------------------------------

function StateInput({
  initial,
  onSubmit,
  disabled,
  required,
}: {
  initial: string;
  onSubmit: (v: string) => void;
  disabled: boolean;
  required: boolean;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const valid = US_STATES.some((s) => s.code === value);
  return (
    <div className="flex flex-col gap-3">
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        className="h-11 w-full rounded-md border border-border bg-background px-3 text-sm shadow-sm"
      >
        <option value="">Select a state…</option>
        {US_STATES.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name} ({s.code})
          </option>
        ))}
      </select>
      <div className="flex justify-end">
        <Button
          type="button"
          disabled={disabled || (required && !valid)}
          onClick={() => onSubmit(value)}
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Number input -----------------------------------------------------------

function NumberInput({
  initial,
  onSubmit,
  disabled,
  required,
}: {
  initial: number | null | undefined;
  onSubmit: (v: number | null) => void;
  disabled: boolean;
  required: boolean;
}) {
  const [text, setText] = useState<string>(
    initial === null || initial === undefined ? "" : String(initial),
  );
  const parsed = text.trim() === "" ? null : Number.parseFloat(text);
  const valid = text.trim() === "" ? !required : Number.isFinite(parsed);
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          $
        </span>
        <Input
          type="number"
          step="0.01"
          inputMode="decimal"
          className="h-11 pl-7 text-base"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          disabled={disabled || !valid}
          onClick={() => onSubmit(parsed)}
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Date input -------------------------------------------------------------

function DateInput({
  initial,
  onSubmit,
  disabled,
  required,
}: {
  initial: string;
  onSubmit: (v: string | null) => void;
  disabled: boolean;
  required: boolean;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const valid = value === "" ? !required : /^\d{4}-\d{2}-\d{2}$/.test(value);
  return (
    <div className="flex flex-col gap-3">
      <Input
        type="date"
        className="h-11 text-base"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          disabled={disabled || !valid}
          onClick={() => onSubmit(value || null)}
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Shared large choice button --------------------------------------------

function BigChoiceButton({
  selected,
  disabled,
  label,
  description,
  onClick,
}: {
  selected: boolean;
  disabled: boolean;
  label: string;
  description?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "group flex items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors",
        "disabled:pointer-events-none disabled:opacity-50",
        selected
          ? "border-primary bg-primary/10 shadow-sm"
          : "border-border hover:border-primary/50 hover:bg-muted/40",
      )}
    >
      <span
        className={cn(
          "mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          selected
            ? "border-primary bg-primary"
            : "border-border group-hover:border-primary/50",
        )}
      >
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold leading-tight">{label}</span>
        {description && (
          <span className="mt-1 block text-xs leading-snug text-muted-foreground">
            {description}
          </span>
        )}
      </span>
      <ArrowRight
        className={cn(
          "mt-1 h-4 w-4 shrink-0 transition-opacity",
          selected ? "opacity-100 text-primary" : "opacity-40 group-hover:opacity-80",
        )}
      />
    </button>
  );
}

// =============================================================================
// Initial-value readers — pull the server-side value for a question so Back
// + edit-in-place pre-fills correctly.
// =============================================================================

function readInitialValue(
  q: IntakeQuestion,
  fam: FamilyProfile | undefined,
  biz: BusinessProfile | undefined,
): unknown {
  switch (q.key) {
    case "focusArea":
      return fam?.focusArea ?? "";
    case "filingStatus":
      return fam?.filingStatus ?? "";
    case "stateOfResidence":
      return fam?.stateOfResidence ?? "";
    case "hasEntity":
      return fam?.hasBusinessEntity ?? undefined;
    case "entityType":
      return biz?.entityType ?? "";
    case "entityFormationState":
      return biz?.formationState ?? "";
    case "isHsaEligible":
      return fam?.isHsaEligible ?? undefined;
    default:
      return undefined;
  }
}

// =============================================================================
// Static option lists. These mirror what the planner can emit but we keep
// copies here so we can render immediately on a branch injection without
// waiting for the refetch to land.
// =============================================================================

const FILING_STATUS_OPTIONS: ReadonlyArray<IntakeQuestionOption> = [
  { value: "Single", label: "Single", description: "One income, no dependents filed as HoH." },
  {
    value: "MarriedFilingJointly",
    label: "Married filing jointly",
    description: "Combined income and deductions on one return.",
  },
  {
    value: "MarriedFilingSeparately",
    label: "Married filing separately",
    description: "Each spouse files their own return. Rare outside specific tax situations.",
  },
  {
    value: "HeadOfHousehold",
    label: "Head of household",
    description: "Unmarried and paying more than half the costs of a qualifying dependent.",
  },
  {
    value: "QualifyingSurvivingSpouse",
    label: "Qualifying surviving spouse",
    description: "Widowed within the past two years with a dependent child.",
  },
];

const ENTITY_TYPE_OPTIONS: ReadonlyArray<IntakeQuestionOption> = [
  {
    value: "SingleMemberLLC",
    label: "Single-member LLC",
    description: "Default files on Schedule C unless you've elected S-Corp.",
  },
  {
    value: "MultiMemberLLC",
    label: "Multi-member LLC",
    description: "Default files Form 1065 partnership return.",
  },
  {
    value: "SCorp",
    label: "S-Corporation",
    description: "Form 1120-S + payroll for owner-employees.",
  },
  {
    value: "CCorp",
    label: "C-Corporation",
    description: "Form 1120 with a separate 21% corporate tax.",
  },
  {
    value: "Partnership",
    label: "Partnership",
    description: "Form 1065 — no corporate shield.",
  },
  {
    value: "SoleProprietor",
    label: "Sole proprietor / DBA",
    description: "Schedule C only, no registered entity.",
  },
];

const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

